/**
 * Public contact page at `/contact` (web).
 * Native cold start routes into `/app/` (same pattern as `/`).
 */

import { useCallback, useEffect, useRef } from 'react'
import { View, Platform } from 'react-native'
import { useRouter, type Href } from 'expo-router'
import { useSupabaseAuth } from '../src/shared/contexts/SupabaseAuthContext'
import { LandingOrgAdminNewNav, LandingHeaderControls, LandingCtaBand, MarketingPageFooter } from '../components/landing'
import { useAccountAdminDrawerShift } from '../src/accounts/contexts/AccountAdminDrawerShiftContext'
import { getWebAccountAdminDrawerPushStyle } from '../src/accounts/helpers/accountAdminDrawerPushWeb.helper'
import { accountAdminSiteShellStyles } from '../appStyles/_accountAdmin.styles'
import {
  LANDING_SITE_NAV_ROUTES,
  LANDING_SITE_NAV_SCROLL_OFFSET_PX,
} from '../src/landing/constants/landingSiteNav.constants'
import { scrollWebWindowToElement, scrollWebWindowToTop } from '../src/landing/helpers/scrollWebMarketingSection.helper'
import { usePublicMarketingWebScrollLayout } from '../src/shared/hooks/usePublicMarketingWebScrollLayout'
import { ErrorBoundary } from '../components/layout'

export default function ContactPage() {
  const { isLoaded } = useSupabaseAuth()
  const { shiftPx } = useAccountAdminDrawerShift()
  const router = useRouter()
  const hasRedirectedRef = useRef(false)

  usePublicMarketingWebScrollLayout()

  const onPressHome = useCallback(() => {
    router.push(LANDING_SITE_NAV_ROUTES.HOME as Href)
  }, [router])

  const onPressContact = useCallback(() => {
    if (Platform.OS !== 'web') return
    scrollWebWindowToTop()
  }, [])

  const onPressFeatures = useCallback(() => {
    void router.push(LANDING_SITE_NAV_ROUTES.HOME as Href)
    if (Platform.OS !== 'web' || typeof document === 'undefined') return
    window.setTimeout(() => {
      scrollWebWindowToElement(
        document.getElementById('features'),
        LANDING_SITE_NAV_SCROLL_OFFSET_PX,
      )
    }, 380)
  }, [router])

  const onPressPricing = useCallback(() => {
    void router.push(LANDING_SITE_NAV_ROUTES.HOME as Href)
    if (Platform.OS !== 'web' || typeof document === 'undefined') return
    window.setTimeout(() => {
      scrollWebWindowToElement(
        document.getElementById('pricing'),
        LANDING_SITE_NAV_SCROLL_OFFSET_PX,
      )
    }, 380)
  }, [router])

  useEffect(() => {
    if (hasRedirectedRef.current || !isLoaded) return
    if (Platform.OS !== 'web') {
      hasRedirectedRef.current = true
      router.replace('/app/')
    }
  }, [isLoaded, router])

  // Web renders regardless of auth: this page is identical signed in or out, and waiting
  // on auth emitted an empty page during static export (see publicStaticRoutes).
  if (Platform.OS !== 'web') return null

  return (
    <ErrorBoundary>
      <View
        style={[
          accountAdminSiteShellStyles.shell,
          getWebAccountAdminDrawerPushStyle(shiftPx),
        ]}
      >
        <LandingOrgAdminNewNav
          onPressHome={onPressHome}
          onPressContact={onPressContact}
          onPressFeatures={onPressFeatures}
          onPressPricing={onPressPricing}
          headerRight={<LandingHeaderControls />}
        />
        <LandingCtaBand />
        <MarketingPageFooter contactMode="route" />
      </View>
    </ErrorBoundary>
  )
}
